// A hub's rating and the Exchange's demonetization rule, from Design > Exchange,
// "Refunds and ratings". These mirror src/config.ts; test/studio-rating.test.ts keeps them equal.

import type { EventRow } from "./types.ts";

export const RATING_PRIOR_REFUNDS = 1;
export const RATING_PRIOR_ANSWERS = 20;
export const DEMONETIZE_MIN_ANSWERS = 50;
export const DEMONETIZE_REFUND_RATE = 0.2;

/** The Exchange's rating: the refund rate with a prior, so a new hub isn't judged on a handful of answers. */
export const rating = (refunds: number, answers: number) => (refunds + RATING_PRIOR_REFUNDS) / (answers + RATING_PRIOR_ANSWERS);

export type RatingStatus = {
  rating: number;
  limit: number;
  /** The rule applies only from this many answers. */
  rated: boolean;
  answersToRated: number;
  demonetized: boolean;
  /** More credits back, at today's answers, before the rating crosses the limit. */
  creditsBackLeft: number;
};

export function ratingStatus(refunds: number, answers: number): RatingStatus {
  const r = rating(refunds, answers);
  const rated = answers >= DEMONETIZE_MIN_ANSWERS;
  // The most refunds with (refunds + 1) / (answers + 20) still at or under the limit; the epsilon absorbs float error.
  const allowed = Math.floor(DEMONETIZE_REFUND_RATE * (answers + RATING_PRIOR_ANSWERS) - RATING_PRIOR_REFUNDS + 1e-9);
  return {
    rating: r, limit: DEMONETIZE_REFUND_RATE, rated, answersToRated: Math.max(0, DEMONETIZE_MIN_ANSWERS - answers),
    demonetized: rated && r > DEMONETIZE_REFUND_RATE, creditsBackLeft: Math.max(0, allowed - refunds),
  };
}


/** Asks the hub answered with an error or with nothing found, newest first. Neither is charged, so neither can be credited back, but each can cost the hub its router model call. */
export const failedAsks = (events: ReadonlyArray<EventRow>) => events.filter((e) => !e.test_run && (e.outcome === "HubError" || e.outcome === "NotFound"));
